import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Badge, Button, Combobox, Modal, SignalDot, type ComboboxOption } from '@indipia/ui-core'
import {
  changeConfirmedPartner, getPartnerCandidates,
  type ForemanChangeResult, type PartnerChangeReasonCode, type PartnerCandidate, type PartnerCandidatesResult,
} from '../api/foremanApi'
import { scheduleApi, type ForemanConfirmSendResult } from '../api/scheduleApi'
import { FOREMAN_CHANGE_MESSAGE, foremanChangeErrorMessage, foremanSendErrorMessage } from '../constants/foremanSendMessage'
import { ForemanChangeNotice, foremanChangeResultMessage } from './ForemanChangeNotice'
import { PhoneIcon } from './PhoneIcon'
import {
  ASSIGN_SOURCE, PARTNER_STATUS, getPartnerStatusLine, getSendAction, type ConfirmRequest,
} from '../constants/partnerStatus'
import { COMM_METHOD_DIRECT_LABEL, isDirectRecord, signalPresentation, type SignalItem } from '@indipia/shared'
import { SIGNAL_COLOR_LABEL, signalCtx, type SignalTaskContext } from '../constants/signalView'
import { useImmediateConfirm, IMMEDIATE_CONFIRM_SUCCESS_MESSAGE } from '../hooks/useImmediateConfirm'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { TaskDetailCard } from './TaskDetailCard'
import { AssignEvidenceModal } from './AssignEvidenceModal'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import {
  PartnerCandidateItem, conflictConfirmText,
} from './PartnerCandidateItem'

// 간트 공종 상세창 "시공파트너" 카드 (기획서 5-1 · 상세창 시공파트너 카드).
// 카드 머리(태그) · 상태 줄(서버 반장 신호 점 + 상태명 + 문구) · 후보 펼침 목록(지연 조회) · 전화번호 줄 · 일정확인 카톡 보내기.
// 선택 값은 부모(GanttView)가 들고 있고 [저장] 시 기존 경로(PATCH /schedule/tasks/{id})로 저장된다.
// 발송 성공 시 서버 응답으로 부모 상태를 갱신한다(onSent).

export interface PartnerCardTask {
  taskId: number
  partnerStatus: string
  assignSource: string | null
  sentDtm: string | null
  respondedDtm: string | null
  /** 마지막 전달된 일정확인 요청 요약(상태 줄의 기한·차순위·재회신 문구 근거). 없으면 null */
  confirmRequest?: ConfirmRequest | null
  assigneePartnerId: number | null
  foremanName: string | null
  foremanContact: string | null
  /** 공종 기간 — 결과만 직접등록 안내('배정하면 바로 확정 · 기간')에 쓴다 */
  startDate: Date
  endDate: Date
  /** 배정 시점 연락 방식(SIF-3) — 'N'=결과만 직접등록(상태 줄에 회신 문구 없음) */
  commYn?: string | null
}

export interface PartnerCardPartner { partnerId: number; partnerName: string; mobile?: string | null; commYn?: string | null }

export interface PartnerSelection { partnerId: number | null; name: string }

/**
 * 상태 줄 점·사유의 원천 — 서버 신호(반장 슬롯). 폴백(PARTNER_STATUS)을 두지 않는다:
 * 신호 조회가 실패하면 점 대신 오류 표시를, 이 공종의 반장 신호가 없으면(none) 점 없이 상태명만 보인다.
 */
export type PartnerCardSignal =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'none' }
  | { state: 'ok'; item: SignalItem; taskCtx: SignalTaskContext }

export interface PartnerCardSent {
  result: ForemanConfirmSendResult
  partnerId: number
  name: string
  mobile: string | null
}

/** 확정 반장 변경(보내기) 성공 — 부모는 result 로 공종 한 건을 갱신하고 message 를 알림으로 띄운다. */
export interface PartnerCardChanged {
  result: ForemanChangeResult
  name: string
  mobile: string | null
  message: string
}

interface Props {
  task: PartnerCardTask
  /** 지금 골라 둔 반장(저장 전 값 포함). null=지정 안 함 */
  selectedPartnerId: number | null
  selectedName: string
  partners: PartnerCardPartner[]
  onSelect: (sel: PartnerSelection) => void
  onSent: (sent: PartnerCardSent) => void
  /** 확정 반장 변경 사유(E01_PARTNER_CHANGE_REASON 코드, 미선택=''). 부모 [저장]이 같은 값을 쓰므로 부모가 들고 있다. */
  changeReason: string
  onChangeReason: (reason: string) => void
  onChanged: (changed: PartnerCardChanged) => void
  /** 이력평가 줄 확장 지점(없으면 렌더하지 않음) */
  historySlot?: ReactNode
  /** 진행 요약 확장 지점(없으면 렌더하지 않음) */
  progressSlot?: ReactNode
  /** 서버 반장 신호(상태 줄 점·사유) */
  signal: PartnerCardSignal
  /** SIF-1 '자재파트너가 시공' 공종 — true 면 시공파트너 선택 대신 한 줄로 축약한다 */
  constructByVendor?: boolean
  /** 자재파트너가 시공일 때 표시할 업체명(자재파트너 거래처명) — 없으면 이름 없이 '자재파트너가 시공'만 */
  constructVendorName?: string | null
}

const NONE_VALUE = ''
/** 미선택 표시용 값 — 목록 항목과 겹치지 않아 입력칸이 비고 안내 문구가 보인다 */
const UNSET_VALUE = '__unset__'
const label12: React.CSSProperties = { fontSize: 12, color: 'var(--shell-on-surface-2)' }
const fmtMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`

export function PartnerCard({ task, selectedPartnerId, selectedName, partners, onSelect, onSent, changeReason, onChangeReason, onChanged, historySlot, progressSlot, signal, constructByVendor, constructVendorName }: Props) {
  const [sending, setSending] = useState(false)
  // 호스트(shell-web)가 주입한 시공이력·평가 슬라이드오버 열기 — 없으면 '이력·평가보기' 링크를 숨긴다
  const host = useSchedulerHost()
  // 착공 D-2 이내 즉시 확정 확인(서버 409 IMMEDIATE_CONFIRM_REQUIRED) — 확정 반장 변경 발송 경로
  const immediate = useImmediateConfirm()
  const invalidateSignals = useInvalidateSignals()   // 발송·반장 변경 성공 후 신호 재조회
  const [error, setError] = useState('')
  // 일정 겹침 후보를 골랐을 때 확인을 기다리는 후보(D-09 §15.6 Modal)
  const [conflictCand, setConflictCand] = useState<PartnerCandidate | null>(null)
  // 자동매칭 배지를 눌러 연 근거 팝업
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  // 상태 점을 눌러 펼친 사유 줄
  const [showReason, setShowReason] = useState(false)
  // 상태 줄의 '방금 발송' 판정용 현재 시각 — 회신 대기 중에만 30초마다 갱신
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (task.partnerStatus !== PARTNER_STATUS.WAITING) return
    setNowMs(Date.now())
    const timer = setInterval(() => setNowMs(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [task.partnerStatus, task.sentDtm])
  // 상세창 열림 동안 캐시 — 선택·발송 성공 후 무효화
  const cacheRef = useRef<PartnerCandidatesResult | null>(null)
  const candMapRef = useRef<Map<string, PartnerCandidate>>(new Map())
  const [candVersion, setCandVersion] = useState(0)

  useEffect(() => {
    cacheRef.current = null
    candMapRef.current = new Map()
    setError('')
    setShowReason(false)
    setEvidenceOpen(false)
  }, [task.taskId])

  const invalidate = () => { cacheRef.current = null }

  const loadOptions = useCallback(async (): Promise<ComboboxOption[]> => {
    if (!cacheRef.current) cacheRef.current = await getPartnerCandidates(task.taskId)
    const list = cacheRef.current.candidates   // 서버 정렬 순서 유지(재정렬 금지)
    candMapRef.current = new Map(list.map(c => [String(c.partnerId), c]))
    setCandVersion(v => v + 1)
    return [
      { value: NONE_VALUE, label: '지정 안 함' },
      ...list.map(c => ({ value: String(c.partnerId), label: c.partnerName })),
    ]
  }, [task.taskId])

  const renderOption = useCallback((opt: ComboboxOption) => {
    if (opt.value === NONE_VALUE) return <span style={{ color: 'var(--shell-on-surface-2)' }}>지정 안 함</span>
    const c = candMapRef.current.get(opt.value)
    return c
      ? <PartnerCandidateItem candidate={c} selected={selectedPartnerId === c.partnerId} compact />
      : opt.label
  // candVersion: 후보 목록이 로드되면 갱신
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId, candVersion])

  const handleChange = (value: string) => {
    if (value === NONE_VALUE) {
      invalidate()
      onChangeReason('')
      onSelect({ partnerId: null, name: '' })
      return
    }
    const c = candMapRef.current.get(value)
    if (!c) return
    if (c.partnerId !== selectedPartnerId && c.scheduleConflict) {
      setConflictCand(c)   // 표준 모달로 확인 — 취소하면 직전 값 유지
      return
    }
    applyCandidate(c)
  }

  const applyCandidate = (c: PartnerCandidate) => {
    invalidate()
    setError('')
    if (c.partnerId === task.assigneePartnerId) onChangeReason('')   // 현재 반장으로 되돌리면 경고·사유 숨김
    onSelect({ partnerId: c.partnerId, name: c.partnerName })
  }

  // ── 태그 ──
  const assigned = task.assigneePartnerId != null
  const tag = !assigned
    ? <Badge status="mute">미배정</Badge>
    : task.assignSource === ASSIGN_SOURCE.AUTO
      // 자동배정(AUTO)일 때만 눌러서 근거 팝업을 연다(직접지정 반장 카드에는 진입이 없다)
      ? (
        <button
          type="button"
          title="자동매칭 근거 보기"
          aria-label="자동매칭 근거 보기"
          onMouseDown={e => e.stopPropagation()}
          onClick={() => setEvidenceOpen(true)}
          style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex' }}
        >
          <Badge status="info">자동매칭</Badge>
        </button>
      )
      : <Badge status="mute">직접지정</Badge>

  // ── 상태 줄 ──
  // 문구는 순수 함수(getPartnerStatusLine)가 만든다 — 기한은 서버 confirmRequest 에서 읽어 재오픈·새로고침 후에도 남는다.
  // 결과만 직접등록(SIF-3) 반장이 배정돼 있으면 일정확인 문구(보내라·회신)를 쓰지 않는다 — 공종 저장값 또는 배정 반장 등록값(배지와 같은 기준)
  const assignedDirect = task.commYn === 'N'
    || isDirectRecord(partners.find(p => p.partnerId === task.assigneePartnerId)?.commYn)
  const { label: statusLabel, text: statusText } = getPartnerStatusLine(assignedDirect ? { ...task, commYn: 'N' } : task, nowMs)

  // 상태 점 클릭 시 보여주는 사유 — 서버 신호(반장 슬롯)의 사유 코드를 940 표현으로 조립한 문구. 신호가 없으면 오류 문구(대체 문구를 만들지 않는다).
  // 자동배정 근거·제외 사유는 '자동매칭' 배지의 근거 팝업(AssignEvidenceModal)이 따로 보여준다.
  const signalError = signal.state === 'error'
  const signalPres = signal.state === 'ok' ? signalPresentation(signal.item.reasonCode, signalCtx(signal.item, signal.taskCtx)) : null
  const reasonText = signal.state === 'ok'
    ? (signalPres ? [signalPres.line, signalPres.reason && signalPres.reason !== signalPres.line ? signalPres.reason : ''].filter(Boolean).join(' · ') : '표시할 수 없는 신호 상태예요')
    : signal.state === 'error' ? '신호 정보를 불러오지 못했어요' : ''

  // ── 전화번호 ──
  const isCurrent = selectedPartnerId != null && selectedPartnerId === task.assigneePartnerId
  const phone = selectedPartnerId == null ? null
    : candMapRef.current.get(String(selectedPartnerId))?.mobile
      ?? partners.find(p => p.partnerId === selectedPartnerId)?.mobile
      ?? (isCurrent ? task.foremanContact : null)

  // ── 결과만 직접등록 배지(SIF-3) — 고른 반장의 연락 방식(파트너 등록값) ──
  const selectedCommYn = selectedPartnerId == null ? null
    : candMapRef.current.get(String(selectedPartnerId))?.commYn
      ?? partners.find(p => p.partnerId === selectedPartnerId)?.commYn
  const directRecord = isDirectRecord(selectedCommYn)
      ?? null

  // ── 확정 반장 변경 여부 — 확정 공종 + 현재 반장 있음 + 현재 반장과 다른 반장 선택 ──
  // 결과만 직접등록 반장을 고르면 사유·발송 없이 그 반장으로 바로 확정되므로 변경 경고·사유를 띄우지 않는다(SIF-3 C-1)
  const isChange = task.partnerStatus === PARTNER_STATUS.CONFIRMED
    && task.assigneePartnerId != null
    && selectedPartnerId != null
    && selectedPartnerId !== task.assigneePartnerId
    && !directRecord

  // ── 발송 ──
  const baseAction = useMemo(
    () => getSendAction({ partnerStatus: task.partnerStatus }, { hasPartner: selectedPartnerId != null, isCurrent }),
    [task.partnerStatus, selectedPartnerId, isCurrent],
  )
  // 확정 반장 변경은 발송 API 가 아니라 partner-changes(사유 필수)로 보낸다
  const action = isChange ? { ...baseAction, label: '일정확인 카톡 보내기', enabled: true } : baseAction

  // 확정 반장 변경 + 바로 보내기(sendNow=true)
  const handleChangeSend = async () => {
    if (selectedPartnerId == null || sending) return
    if (!changeReason) { setError(FOREMAN_CHANGE_MESSAGE.reasonInvalid); return }
    setSending(true); setError('')
    try {
      const run = await immediate.run(ok => changeConfirmedPartner(task.taskId, {
        newPartnerId: selectedPartnerId,
        reasonCode: changeReason as PartnerChangeReasonCode,
        sendNow: true,
        ...(ok && { confirmImmediate: true }),
      }))
      if (run.cancelled) return   // 취소 — 선택·사유는 그대로 두어 다시 고를 수 있게 한다
      const result = run.value
      invalidate()
      void invalidateSignals()
      onChangeReason('')
      onChanged({
        result, name: selectedName, mobile: phone,
        message: result.fixed ? IMMEDIATE_CONFIRM_SUCCESS_MESSAGE : foremanChangeResultMessage(result, selectedName, task.foremanName),
      })
    } catch (err) {
      setError(foremanChangeErrorMessage(err))
    } finally {
      setSending(false)
    }
  }

  const handleSend = async () => {
    if (isChange) { void handleChangeSend(); return }
    if (!action.enabled || !action.method || selectedPartnerId == null || sending) return
    setSending(true); setError('')
    try {
      const result = await scheduleApi.sendForemanConfirm(task.taskId, {
        sendMethod: action.method,
        // RESEND 는 partnerId 필수(= 현재 반장 — getSendAction 이 isCurrent 일 때만 RESEND 를 돌려준다)
        ...((action.withPartnerId || action.method === 'RESEND') && { partnerId: selectedPartnerId }),
      })
      invalidate()
      void invalidateSignals()
      onSent({ result, partnerId: result.partnerId ?? selectedPartnerId, name: selectedName, mobile: phone })
    } catch (err) {
      setError(foremanSendErrorMessage(err))
    } finally {
      setSending(false)
    }
  }

  // SIF-1 '자재파트너가 시공' — 시공파트너 선택칸·'미배정' 라벨 대신 한 줄로 축약(빗금 신호 NONE).
  // 업체명은 그 공종 자재파트너 거래처명. 서버가 색 NONE(빗금) + 사유 FOREMAN_NA 로 내려주므로 신호가 있으면 그대로, 없으면 NONE 을 쓴다.
  if (constructByVendor) {
    const dotColor = signal.state === 'ok' ? signal.item.colorCode : 'NONE'
    return (
      <TaskDetailCard title="시공파트너" emphasis>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <SignalDot color={dotColor} size={14} label={`시공파트너 신호 ${SIGNAL_COLOR_LABEL.NONE}`} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface)', minWidth: 0 }}>
            자재파트너가 시공{constructVendorName ? ` — ${constructVendorName}` : ''}
          </span>
        </div>
        <div style={{ ...label12, marginTop: 4, color: 'var(--shell-hint)' }}>시공파트너 섭외 · 일정확인 카톡 없음</div>
      </TaskDetailCard>
    )
  }

  return (
    <TaskDetailCard title="시공파트너" emphasis tag={tag} historySlot={historySlot} progressSlot={progressSlot}>

      {/* 상태 줄 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: showReason ? 2 : 6, minWidth: 0 }} aria-live="polite">
        {/* 상태 점·상태명을 누르면 사유를 보여준다(FR-B-91) */}
        <button
          type="button"
          onMouseDown={e => e.stopPropagation()}
          onClick={() => setShowReason(v => !v)}
          aria-expanded={showReason}
          aria-label={`${statusLabel} 사유 보기`}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
        >
          {signal.state === 'ok' && (
            <SignalDot color={signal.item.colorCode} label={`시공파트너 신호 ${SIGNAL_COLOR_LABEL[signal.item.colorCode]}`} />
          )}
          {signal.state === 'loading' && <span aria-hidden="true" style={{ width: 10, height: 10, flexShrink: 0 }} />}
          {signal.state === 'error' && (
            <span role="img" aria-label="신호 오류" title="신호 정보를 불러오지 못했어요" style={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', background: 'var(--status-danger-600)', color: 'var(--shell-surface)', fontSize: 8, fontWeight: 700, lineHeight: '10px', textAlign: 'center' }}>!</span>
          )}
          <b style={{ fontSize: 12, color: 'var(--shell-on-surface)' }}>{statusLabel}</b>
        </button>
        {statusText && <span style={{ ...label12, minWidth: 0 }}>{statusText}</span>}
      </div>
      {showReason && reasonText && (
        <div style={{ ...label12, marginBottom: 6, paddingLeft: 16, ...(signalError ? { color: 'var(--status-danger-600)' } : null) }} role={signalError ? 'alert' : 'status'}>{reasonText}</div>
      )}

      {/* 후보 펼침 목록 · 결과만 직접등록 배지 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Combobox
            // 미선택이면 안내 문구(placeholder)를 보인다 — 값을 목록의 '지정 안 함'(NONE_VALUE)과 다르게 둬서 입력칸이 비도록 한다. '지정 안 함' 항목은 목록에 그대로 있다.
            value={selectedPartnerId == null ? UNSET_VALUE : String(selectedPartnerId)}
            displayValue={selectedPartnerId == null ? '' : (selectedName || '지정 안 함')}
            placeholder="시공파트너를 골라 주세요"
            loadOptions={loadOptions}
            renderOption={renderOption}
            onChange={handleChange}
            emptyText="맡길 수 있는 반장님이 없어요"
            aria-label="시공파트너 선택"
            onMouseDown={e => e.stopPropagation()}
          />
        </div>
        {directRecord && <Badge status="warning">{COMM_METHOD_DIRECT_LABEL}</Badge>}
      </div>

      {/* 전화번호 줄 */}
      <div style={{ marginTop: 5, paddingLeft: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
        {selectedPartnerId != null ? (
          phone ? (
            <>
              <PhoneIcon />
              <span style={label12}>{phone}</span>
            </>
          ) : null
        ) : (
          <span style={{ ...label12, color: 'var(--shell-hint)' }}>배정된 시공파트너가 없어요</span>
        )}
        {selectedPartnerId != null && host?.openPartnerHistory && (
          <button
            type="button"
            onMouseDown={e => e.stopPropagation()}
            onClick={() => host.openPartnerHistory?.({
              partnerId: selectedPartnerId,
              partnerName: selectedName || partners.find(p => p.partnerId === selectedPartnerId)?.partnerName || '',
            })}
            style={{ marginLeft: 'auto', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: 'var(--shell-on-surface-2)', textDecoration: 'underline', flexShrink: 0 }}
          >이력·평가보기 ›</button>
        )}
      </div>

      {/* 확정 반장 변경 경고 + 사유 */}
      {isChange && (
        <ForemanChangeNotice
          className="mt-2"
          previousName={task.foremanName}
          reason={changeReason}
          onReasonChange={r => { onChangeReason(r); setError('') }}
          disabled={sending}
        />
      )}

      {/* 일정확인 카톡 보내기 — 결과만 직접등록 반장은 버튼 자리에 안내(SIF-3 C-1: 배정 = 바로 일정 확정) */}
      {directRecord ? (
        <Alert kind="success" icon={<SignalDot color="GREEN" />} style={{ marginTop: 8 }}>
          {/* Alert 본문은 세로 배치라 한 덩어리로 감싸 한 줄로 잇는다 */}
          <span><b>일정 확정</b> · 배정하면 바로 확정 · {fmtMonthDay(task.startDate)} ~ {fmtMonthDay(task.endDate)}</span>
        </Alert>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="w-full"
          style={{ marginTop: 8 }}
          disabled={!action.enabled}
          loading={sending}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleSend}
        >{action.label}</Button>
      )}
      {error && <div className="field error" style={{ marginTop: 6, maxWidth: 'none' }} role="alert"><span className="help">{error}</span></div>}

      {/* 일정 겹침 확인 모달 — SigongPopup(SCR-16)의 확인 모달과 같은 구성 */}
      <Modal
        open={conflictCand != null}
        onClose={() => setConflictCand(null)}
        title="일정이 겹쳐요"
        footerAlign="stretch"
        footer={conflictCand && (
          <>
            <Button variant="secondary" onClick={() => setConflictCand(null)}>취소</Button>
            <Button
              variant="primary"
              onClick={() => { const c = conflictCand; setConflictCand(null); applyCandidate(c) }}
            >
              배정
            </Button>
          </>
        )}
      >
        {conflictCand && (
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--shell-on-surface)' }}>
            <span style={{ whiteSpace: 'pre-line' }}>{conflictConfirmText(conflictCand, '배정')}</span>
          </div>
        )}
      </Modal>
      {immediate.modal}
      {task.assignSource === ASSIGN_SOURCE.AUTO && (
        <AssignEvidenceModal open={evidenceOpen} onClose={() => setEvidenceOpen(false)} taskId={task.taskId} partnerName={task.foremanName} />
      )}
    </TaskDetailCard>
  )
}
